/**
 * Ravan Global Core
 *
 * A decoupled version of RavanCore that works independently of a specific quotation.
 * Uses `ravan-actions` edge function for global actions.
 */

import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/utils/logger';
import { commandInterpreter, type ParsedCommand, type RavanAction } from './commandInterpreter';
import { type ChatMessage } from './ravanCore';

export interface RavanGlobalResponse {
  reply: string;
  command?: ParsedCommand;
  suggestedFollowUps: string[];
  workflowHint?: string;
  modelUsed?: string;
}

export type RavanMode = 'global' | 'catalogue' | 'analytics' | 'estimate';

class RavanGlobalCore {
  async processMessage(
    userMessage: string,
    conversationHistory: ChatMessage[],
    mode: RavanMode = 'global',
    pinnedQuoteId?: string
  ): Promise<RavanGlobalResponse> {
    const turnIndex = conversationHistory.filter(m => m.role === 'user').length;
    logger.log(`[RavanGlobal] Turn ${turnIndex} (Mode: ${mode}): "${userMessage.slice(0, 60)}"`);

    // ── 1. Interpret command ───────────────────────────────────────────────────
    const command = await commandInterpreter.interpret(userMessage, {} as any);
    logger.log(`[RavanGlobal] Interpreted as: ${command.action} (${command.confidence})`);

    // ── 2. Conversation history for Edge Function (last 8 turns) ──────────────
    const recentHistory = conversationHistory
      .filter(m => m.role !== 'system' && !m.isLoading)
      .slice(-8)
      .map(m => ({ role: m.role === 'ravan' ? 'assistant' : 'user', content: m.content }));

    // ── 3. Call Ravan Edge Function ────────────────────────────────────────────
    const { data, error } = await supabase.functions.invoke('ravan-chat', {
      body: {
        userMessage,
        estimateContext: pinnedQuoteId ? { quoteId: pinnedQuoteId } : {},
        ragContext:      '',
        sessionMemory:   '',
        command: {
          action:       command.action,
          parameters:   command.parameters,
          displayLabel: command.displayLabel,
        },
        plan: null,
        conversationHistory: recentHistory,
        workflowHint: undefined,
        mode,
        modelConfig: {
          model:       'gpt-4o', // Defaulting for global for now
          maxTokens:   1200,
          temperature: 0.7,
        },
      },
    });

    if (error || !data) {
      logger.warn(`[RavanGlobal] Edge Function error: ${error?.message}`);
      return {
        reply:              this._fallbackReply(command),
        command:            command.requiresApproval ? command : undefined,
        suggestedFollowUps: this._defaultFollowUps(command.action, mode),
        modelUsed:          'gpt-4o',
      };
    }

    return {
      reply:              data.reply ?? this._fallbackReply(command),
      command:            command.requiresApproval ? command : undefined,
      suggestedFollowUps: data.followUps ?? this._defaultFollowUps(command.action, mode),
      workflowHint:       data.workflowHint,
      modelUsed:          'gpt-4o',
    };
  }

  // Helper method to execute global actions via ravan-actions edge function
  async executeGlobalAction(command: ParsedCommand, quoteId?: string) {
    const action = command.action;
    const parameters = { ...command.parameters };
    if (quoteId) parameters.quote_id = quoteId;

    try {
      const { data, error } = await supabase.functions.invoke('ravan-actions', {
        body: { action, parameters }
      });
      if (error || !data?.ok) {
        return { ok: false, message: data?.message || error?.message || 'Action failed.' };
      }
      return { ok: true, message: data.message, result: data.result };
    } catch (err: unknown) {
      return { ok: false, message: (err instanceof Error ? err.message : String(err)) };
    }
  }

  private _fallbackReply(command: ParsedCommand): string {
    if (command.action === 'general_question') {
      return "I'm Ravan, your AI assistant. How can I help you today?";
    }
    return `I've prepared to ${command.displayLabel}. Review the details below and confirm to proceed.`;
  }

  private _defaultFollowUps(action: RavanAction, mode: RavanMode): string[] {
    const map: Partial<Record<RavanAction, string[]>> = {
      add_catalogue_item: ['Analyze pricing trends', 'Search catalogue'],
      create_quote: ['Search my catalogue', 'Draft proposal'],
      analyze_price_trend: ['Add an item to my catalogue', 'Search catalogue'],
    };
    return map[action] ?? [
      mode === 'catalogue' ? 'Add another item' : 'Create a new quote for a client',
      'Analyze pricing trends for wardrobe items',
      'Generate a client summary'
    ];
  }
}

export const ravanGlobalCore = new RavanGlobalCore();
